import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { MessageCircle, Trash2, Flame, BellOff } from "lucide-react";
import StatusViewer from "@/components/StatusViewer";
import { isMuted } from "@/lib/notifications";

const timeAgo = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h`;
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
};

export default function ChatsTab() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [pendingDelete, setPendingDelete] = useState(null);
  const [viewer, setViewer] = useState(null);

  const { data: convos = [], isLoading } = useQuery({
    queryKey: ["conversations"],
    queryFn: async () => (await api.get("/conversations")).data,
    refetchInterval: 5000,
  });

  const { data: statuses = [] } = useQuery({
    queryKey: ["statuses"],
    queryFn: async () => (await api.get("/status")).data,
    refetchInterval: 30000,
  });

  const confirmDelete = async () => {
    const friendId = pendingDelete?.user_id;
    setPendingDelete(null);
    if (!friendId) return;
    try {
      await api.delete(`/conversations/${friendId}`);
      toast.success("Discussion supprimée");
      qc.invalidateQueries({ queryKey: ["conversations"] });
    } catch {
      toast.error("Suppression impossible");
    }
  };

  const deleteStatus = async (statusId) => {
    try {
      await api.delete(`/status/${statusId}`);
      qc.invalidateQueries({ queryKey: ["statuses"] });
      setViewer(null);
      toast.success("Statut supprimé");
    } catch {
      toast.error("Suppression impossible");
    }
  };

  return (
    <div>
      {statuses.length > 0 && (
        <div className="mb-8">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-orange-400 mb-4 flex items-center gap-1.5">
            <Flame className="h-3.5 w-3.5" /> Statuts
          </p>
          <div className="flex gap-4 overflow-x-auto pb-2">
            {statuses.map((g) => {
              const preview = g.items[g.items.length - 1];
              const isVideo = preview?.media_type === "video";
              return (
                <button
                  key={g.user.user_id}
                  data-testid="status-ring"
                  onClick={() => setViewer(g)}
                  className="flex flex-col items-center gap-2 shrink-0 w-16"
                >
                  <div className="p-[2px] rounded-full bg-gradient-to-tr from-orange-500 via-[#C4A47C] to-orange-400">
                    <div className="p-[2px] rounded-full bg-[#050505]">
                      {isVideo ? (
                        <video
                          data-testid="status-ring-video"
                          src={`${API}/files/${preview.file_path}`}
                          className="h-14 w-14 rounded-full object-cover bg-[#1A1A1A]"
                          muted
                          autoPlay
                          loop
                          playsInline
                        />
                      ) : (
                        <Avatar className="h-14 w-14">
                          <AvatarImage src={g.user.picture} />
                          <AvatarFallback className="bg-[#1A1A1A] text-[#C4A47C]">{g.user.name?.[0]?.toUpperCase()}</AvatarFallback>
                        </Avatar>
                      )}
                    </div>
                  </div>
                  <span className="text-[11px] text-[#8A8A8A] truncate w-full text-center">{g.mine ? "Moi" : g.user.name?.split(" ")[0]}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#8A8A8A] mb-6">Discussions</p>

      {isLoading ? (
        <div className="text-[#8A8A8A] text-sm">Chargement…</div>
      ) : convos.length === 0 ? (
        <div className="text-center py-20">
          <MessageCircle className="h-10 w-10 text-[#8A8A8A]/40 mx-auto mb-4" strokeWidth={1.5} />
          <p className="text-[#8A8A8A]">Aucune discussion pour l'instant.</p>
          <p className="text-[#8A8A8A]/60 text-sm mt-1">Ajoutez des amis pour commencer.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {convos.map((c) => (
            <div
              key={c.friend.user_id}
              data-testid="chat-conversation-item"
              className="group w-full flex items-center gap-4 bg-[#121212] border border-white/5 rounded-2xl p-4 hover:bg-[#1A1A1A] transition-all duration-300"
            >
              <button
                onClick={() => navigate(`/app/chat/${c.friend.user_id}`)}
                className="flex items-center gap-4 flex-1 min-w-0 text-left"
              >
                <Avatar className="h-12 w-12 border border-white/10">
                  <AvatarImage src={c.friend.picture} />
                  <AvatarFallback className="bg-[#1A1A1A] text-[#C4A47C]">
                    {c.friend.name?.[0]?.toUpperCase() || "?"}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className="font-medium truncate flex items-center gap-1.5">
                      {c.friend.name}
                      {c.friend.in_heat && <span className="text-xs" title="En chaleur">🦁</span>}
                      {isMuted(c.friend.user_id) && <BellOff className="h-3.5 w-3.5 text-[#8A8A8A] shrink-0" />}
                    </span>
                    <span className="text-xs text-[#8A8A8A] shrink-0 ml-2">{timeAgo(c.last_time)}</span>
                  </div>
                  <span className="text-sm text-[#8A8A8A] truncate block mt-0.5">
                    {c.last_message || "Démarrez la conversation"}
                  </span>
                </div>
              </button>
              {c.unread > 0 && (
                <span className="shrink-0 h-6 min-w-6 px-2 rounded-full bg-[#C4A47C] text-black text-xs font-bold flex items-center justify-center">
                  {c.unread}
                </span>
              )}
              <button
                data-testid="delete-conversation-button"
                onClick={() => setPendingDelete(c.friend)}
                title="Supprimer la discussion"
                className="shrink-0 p-2 rounded-full text-[#8A8A8A] hover:text-red-400 hover:bg-red-500/10 opacity-60 sm:opacity-0 group-hover:opacity-100 transition duration-200"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {viewer && (
        <StatusViewer group={viewer} apiBase={API} onClose={() => setViewer(null)} onDelete={deleteStatus} />
      )}

      <AlertDialog open={!!pendingDelete} onOpenChange={(open) => !open && setPendingDelete(null)}>
        <AlertDialogContent className="bg-[#121212] border border-white/10 text-white" data-testid="delete-conversation-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Supprimer la discussion ?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#8A8A8A]">
              Tous les messages échangés avec {pendingDelete?.name} seront supprimés définitivement pour vous deux.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="delete-conversation-cancel"
              className="bg-[#1A1A1A] border border-white/10 text-white hover:bg-[#2A2A2A] hover:text-white rounded-full"
            >
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-conversation-confirm"
              onClick={confirmDelete}
              className="bg-red-500 text-white hover:bg-red-600 rounded-full"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
